import { CAMERA } from '../constants';
import { clamp, damp, type Vec2 } from '../utils/math';

/**
 * Camera maps world coordinates → screen pixels.
 *
 * Projection:
 *   screen = (world - cameraCenter) * zoom + viewportCenter + shakeOffset
 *
 * Features:
 *   - Damped follow toward `target` (frame-rate independent).
 *   - Damped zoom toward `zoomTarget`.
 *   - Screen-shake: tracks a residual amplitude that decays exponentially;
 *     each frame produces a fresh random offset within that amplitude.
 *   - The engine layer computes a "lookahead" target (head + velocity * t)
 *     before calling update(), so the camera reads as alive rather than
 *     glued to the head.
 */
export class Camera {
  public position: Vec2 = { x: 0, y: 0 };
  public target: Vec2 = { x: 0, y: 0 };

  public zoom: number = CAMERA.START_ZOOM;
  public zoomTarget: number = CAMERA.START_ZOOM;

  public viewportW: number = 0;
  public viewportH: number = 0;

  /** Active shake amplitude in CSS pixels; decays each frame. */
  private shakeAmp = 0;
  /** Resampled each frame and applied during projection. */
  private shakeOffsetX = 0;
  private shakeOffsetY = 0;

  /**
   * Screen-space flash — full-viewport alpha tint that decays each frame.
   * Fires on player death + nearby kills to maximize dopamine feedback
   * without needing a separate full-screen post-processing pass.
   */
  private flashAmp = 0;
  private flashHue = 0;

  setViewport(w: number, h: number): void {
    this.viewportW = w;
    this.viewportH = h;
  }

  snapTo(x: number, y: number): void {
    this.position.x = this.target.x = x;
    this.position.y = this.target.y = y;
  }

  /** Add a shake impulse (in pixels); the largest pending impulse wins. */
  addShake(amplitude: number): void {
    if (amplitude > this.shakeAmp) this.shakeAmp = Math.min(amplitude, CAMERA.SHAKE_MAX_PX);
  }

  /**
   * Fire a screen-space color flash. `amplitude` ∈ (0, 1] caps the peak
   * alpha of the overlay. Larger impulses dominate smaller ones, mirroring
   * the shake rule so a death always overrides a kill flash.
   */
  addFlash(amplitude: number, hue: number): void {
    if (amplitude > this.flashAmp) {
      this.flashAmp = Math.min(0.6, amplitude);
      this.flashHue = hue;
    }
  }

  /** Currently active flash alpha (0..1). */
  get flashAlpha(): number { return this.flashAmp; }
  /** Currently active flash hue. */
  get flashColor(): number { return this.flashHue; }

  update(dt: number): void {
    this.position.x = damp(this.position.x, this.target.x, CAMERA.FOLLOW_STIFFNESS, dt);
    this.position.y = damp(this.position.y, this.target.y, CAMERA.FOLLOW_STIFFNESS, dt);
    this.zoom = damp(this.zoom, this.zoomTarget, CAMERA.ZOOM_STIFFNESS, dt);

    if (this.shakeAmp > 0.05) {
      this.shakeAmp *= Math.exp(-CAMERA.SHAKE_DECAY * dt);
      this.shakeOffsetX = (Math.random() - 0.5) * 2 * this.shakeAmp;
      this.shakeOffsetY = (Math.random() - 0.5) * 2 * this.shakeAmp;
    } else {
      this.shakeAmp = 0;
      this.shakeOffsetX = 0;
      this.shakeOffsetY = 0;
    }

    // Flash decays roughly 2× faster than shake so it never lingers as a tint.
    if (this.flashAmp > 0.005) {
      this.flashAmp *= Math.exp(-CAMERA.SHAKE_DECAY * 2.2 * dt);
    } else {
      this.flashAmp = 0;
    }
  }

  setZoomTarget(z: number): void {
    this.zoomTarget = clamp(z, CAMERA.MIN_ZOOM, CAMERA.MAX_ZOOM);
  }

  /** Project a world point into screen (CSS-pixel) coordinates. */
  worldToScreen(wx: number, wy: number, out: Vec2 = { x: 0, y: 0 }): Vec2 {
    out.x = (wx - this.position.x) * this.zoom + this.viewportW * 0.5 + this.shakeOffsetX;
    out.y = (wy - this.position.y) * this.zoom + this.viewportH * 0.5 + this.shakeOffsetY;
    return out;
  }

  /** Unproject a screen (CSS-pixel) point into world coordinates. */
  screenToWorld(sx: number, sy: number, out: Vec2 = { x: 0, y: 0 }): Vec2 {
    out.x = (sx - this.viewportW * 0.5 - this.shakeOffsetX) / this.zoom + this.position.x;
    out.y = (sy - this.viewportH * 0.5 - this.shakeOffsetY) / this.zoom + this.position.y;
    return out;
  }

  /** AABB in world coords currently visible — used for culling. */
  getVisibleBounds(): { minX: number; minY: number; maxX: number; maxY: number } {
    const halfW = this.viewportW * 0.5 / this.zoom;
    const halfH = this.viewportH * 0.5 / this.zoom;
    return {
      minX: this.position.x - halfW,
      minY: this.position.y - halfH,
      maxX: this.position.x + halfW,
      maxY: this.position.y + halfH,
    };
  }

  /** Convenience: current shake offsets exposed for renderers that bypass project. */
  get shakeX(): number { return this.shakeOffsetX; }
  get shakeY(): number { return this.shakeOffsetY; }
}
